import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
const homeLinks = [{
  href: '#platform',
  label: 'Platform'
}, {
  href: '#gone-fishing',
  label: 'Gone Fishing'
}, {
  href: '#pricing',
  label: 'Pricing'
}];
export function Navbar() {
  const [open, setOpen] = useState(false);
  const [waterMode, setWaterMode] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuLinkRef = useRef<HTMLAnchorElement>(null);
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  const isHome = location.pathname === '/';
  useEffect(() => {
    if (!isHome) {
      setWaterMode(false);
      return;
    }
    const updateMode = () => {
      const chapter = document.getElementById('gone-fishing');
      if (!chapter) return;
      const bounds = chapter.getBoundingClientRect();
      setWaterMode(bounds.top < 80 && bounds.bottom > 80);
    };
    updateMode();
    window.addEventListener('scroll', updateMode, {
      passive: true
    });
    return () => window.removeEventListener('scroll', updateMode);
  }, [isHome]);
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    const focusMenu = window.setTimeout(() => firstMenuLinkRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusMenu);
    };
  }, [open]);
  const foreground = waterMode ? 'text-white' : 'text-[#11110f]';
  const backdrop = waterMode ? 'border-white/15 bg-[#062c50]/95' : 'border-black/10 bg-[#f7f5ef]/95';
  const closeMenu = () => setOpen(false);
  return <motion.header initial={reduceMotion ? false : {
    y: -64,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.45,
    ease: [0.22, 1, 0.36, 1]
  }} className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${backdrop}`} data-id="element-593">
      <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-10" aria-label="Main navigation" data-id="element-594">
        <Link to="/" className={`flex items-center gap-2.5 transition-opacity hover:opacity-70 ${foreground}`} aria-label="Sampler home" data-id="element-595">
          <span className={`grid h-8 w-8 place-items-center rounded-sm ${waterMode ? 'bg-white text-[#062c50]' : 'bg-[#11110f] text-white'}`} data-id="element-596">
            <span className="h-2.5 w-2.5 rounded-full bg-current" data-id="element-597" />
          </span>
          <span className="font-display text-xl font-bold" data-id="element-598">SAMPLER</span>
        </Link>

        <div className="hidden items-center gap-5 lg:flex" data-id="element-599">
          {isHome && homeLinks.map(link => <a key={link.href} href={link.href} className={`text-sm font-semibold transition-opacity hover:opacity-60 ${foreground}`} data-id="element-600">
                {link.label}
              </a>)}
          <Link to="/brand" className={`text-sm font-semibold transition-opacity hover:opacity-60 ${location.pathname === '/brand' ? 'text-[#ff4d17]' : foreground}`} data-id="element-601">
            Brand Portal
          </Link>
          <Link to="/investor" className={`text-sm font-semibold transition-opacity hover:opacity-60 ${location.pathname === '/investor' ? 'text-[#ff4d17]' : foreground}`} data-id="element-602">
            Investor
          </Link>
        </div>

        {isHome ? <a href="#start" className="hidden rounded-full bg-[#ff4d17] px-5 py-2.5 text-xs font-extrabold tracking-[0.08em] text-white transition-colors hover:bg-[#dd3d0f] active:bg-[#c9340b] lg:inline-flex" data-id="element-603">
            START A CAMPAIGN
          </a> : <Link to="/" className="hidden rounded-full bg-[#ff4d17] px-5 py-2.5 text-xs font-extrabold tracking-[0.08em] text-white transition-colors hover:bg-[#dd3d0f] active:bg-[#c9340b] lg:inline-flex" data-id="element-604">
            SAMPLER HOME
          </Link>}

        <button ref={menuButtonRef} type="button" className={`grid h-10 w-10 place-items-center rounded-full border transition-colors hover:bg-black/5 active:scale-95 lg:hidden ${waterMode ? 'border-white/30 text-white hover:bg-white/10' : 'border-black/20 text-black'}`} aria-label={open ? 'Close menu' : 'Open menu'} aria-controls="mobile-navigation" aria-expanded={open} onClick={() => setOpen(current => !current)} data-id="element-605">
          {open ? <XIcon size={19} aria-hidden="true" data-id="element-606" /> : <MenuIcon size={19} aria-hidden="true" data-id="element-607" />}
        </button>
      </nav>

      <AnimatePresence data-id="element-608">
        {open && <motion.div id="mobile-navigation" initial={reduceMotion ? false : {
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduceMotion ? undefined : {
        opacity: 0,
        y: -8
      }} className={`${waterMode ? 'bg-[#062c50]' : 'bg-[#f7f5ef]'} border-t ${waterMode ? 'border-white/15' : 'border-black/10'} px-5 py-4 lg:hidden`} role="navigation" aria-label="Mobile navigation" data-id="element-609">
            <div className="flex flex-col gap-1" data-id="element-610">
              {isHome && homeLinks.map((link, index) => <a ref={index === 0 ? firstMenuLinkRef : undefined} key={link.href} href={link.href} onClick={closeMenu} className={`rounded-xl px-3 py-3 text-base font-bold transition-colors hover:bg-black/5 ${waterMode ? 'hover:bg-white/10' : ''} ${foreground}`} data-id="element-611">
                    {link.label}
                  </a>)}
              <Link ref={!isHome ? firstMenuLinkRef : undefined} to="/brand" onClick={closeMenu} className={`rounded-xl px-3 py-3 text-base font-bold transition-colors hover:bg-black/5 ${waterMode ? 'hover:bg-white/10' : ''} ${foreground}`} data-id="element-612">
                BRAND PORTAL
              </Link>
              <Link to="/investor" onClick={closeMenu} className={`rounded-xl px-3 py-3 text-base font-bold transition-colors hover:bg-black/5 ${waterMode ? 'hover:bg-white/10' : ''} ${foreground}`} data-id="element-613">
                INVESTOR
              </Link>
              {isHome ? <a href="#start" onClick={closeMenu} className="mt-3 rounded-xl bg-[#ff4d17] px-4 py-3 text-center text-sm font-extrabold text-white transition-colors hover:bg-[#dd3d0f] active:bg-[#c9340b]" data-id="element-614">
                  START A CAMPAIGN
                </a> : <Link to="/" onClick={closeMenu} className="mt-3 rounded-xl bg-[#ff4d17] px-4 py-3 text-center text-sm font-extrabold text-white transition-colors hover:bg-[#dd3d0f] active:bg-[#c9340b]" data-id="element-615">
                  SAMPLER HOME
                </Link>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}